'use client';
import { Assessment, ListAlt } from '@mui/icons-material';
import { Box, Button, Chip, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Curriculum from '@/components/curriculum/curriculum';
import CircularProgressLabel from '@/components/shared/circular-progress-label';
import ButtonRequestTestSubmission from '@/components/test/button-request-test-submission';
import ButtonStartNewTestRun from '@/components/test/button-start-new-test-run';
import TimerPanel from '@/components/test/timer-panel';
import { CourseTabList } from '@/constants/menu';
import { useTestResults } from '@/hooks';
import { useAuth, useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import { useRoute } from '@/utils/client/route';

export default function CourseMenu() {
  const { t } = useTranslation();
  const { goToRoute } = useRoute();
  const { isLoggedIn } = useAuth();
  const { selectedCourse, courseStatus, canRenewTest } = useCourses();
  const { toggleMenuDrawer, timer } = useViewStore();
  const { data: testResults } = useTestResults(selectedCourse?.id);

  if (!selectedCourse) return null;

  const { type } = selectedCourse;
  const isTest = type === 'Test';
  const hasTestResults = (testResults?.length ?? 0) >= 1;

  const onNavigate = (id: string, params?: Record<string, string | number>) => {
    toggleMenuDrawer();
    goToRoute(id, params);
  };

  const onStopLearning = async () => {
    if (isTest) {
      timer.setRunning(false);
    }
    onNavigate('myCourseTabs', {
      courseSlug: selectedCourse.slug,
      tabId: CourseTabList.inhalte,
    });
  };

  const onViewTestResults = () => {
    onNavigate('myCourseTabs', {
      courseSlug: selectedCourse.slug,
      tabId: CourseTabList.ergebnisse,
    });
  };

  const showTestDetails =
    isLoggedIn && isTest && courseStatus?.hasMembership && courseStatus?.status === 'active';

  return (
    <Box>
      {isLoggedIn && (
        <Paper
          elevation={4}
          sx={{
            borderRadius: 0,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: 0,
          }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              py: 1,
              px: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Box>
              Status:{' '}
              {courseStatus?.status === 'active' && (
                <Chip label="Aktiv" color="success" size="small" variant="outlined" />
              )}
              {courseStatus?.status === 'submitted' && (
                <Chip label="Ausgewertet" size="small" variant="outlined" />
              )}
            </Box>
            {isTest && courseStatus?.repetitions && (
              <Box>
                {t('test.menu.runs')}:{' '}
                <Chip
                  label={`${courseStatus?.repetitions.run}/${courseStatus?.repetitions.run + courseStatus?.repetitions.remaining}`}
                  size="small"
                  sx={{ mr: 2 }}
                />
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
              }}>
              Forts.: <CircularProgressLabel value={courseStatus?.chapters.progress ?? 0} />
            </Box>
          </Box>
        </Paper>
      )}
      <Paper
        elevation={4}
        sx={{
          position: 'sticky',
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          top: { xs: 56, sm: 64 },
          borderRadius: 0,
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: 2,
            py: 2,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            '& .MuiButton-sizeSmall .MuiButton-label': {
              fontSize: 12,
              textTransform: 'none',
            },
            gap: 1,
            flexWrap: 'wrap',
          }}>
          <Button size="small" startIcon={<ListAlt />} variant="outlined" onClick={onStopLearning}>
            {isTest ? t('test.menu.contents') : t('test.menu.courseContents')}
          </Button>
          {isTest && hasTestResults && (
            <Button
              size="small"
              startIcon={<Assessment />}
              variant="outlined"
              onClick={onViewTestResults}>
              {t('test.menu.results')}
            </Button>
          )}
          {canRenewTest && <ButtonStartNewTestRun />}
          {showTestDetails && <ButtonRequestTestSubmission size="small" />}
        </Box>
        {showTestDetails && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}>
            <TimerPanel />
          </Box>
        )}
      </Paper>
      <Box>
        <Curriculum
          indentation={false}
          course={selectedCourse}
          hasMembership={courseStatus?.hasMembership ?? false}
        />
      </Box>
    </Box>
  );
}
